import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '@/hooks/useProducts';
import { useCart } from '@/context/CartContext';
import { useMetaPixel } from '@/context/MetaPixelContext';
import { useCategories } from '@/hooks/useCategories';
import { useVariants } from '@/hooks/useVariants';
import { getCompanySettings, getLandingContent, getStoreNotices, DEFAULT_LANDING_CONTENT, DEFAULT_STORE_NOTICES, PCP_OFFICIAL_WHATSAPP, type LandingContent, type StoreNotices } from '@/services/finances';
import { getHeroFontFamily, getHeroTextShadow, getHeroOverlayClass, getHeroTitleSizeClass, getSectionTitleSizeClass, getTextWeightClass } from '@/services/heroText';
import { getProductDiscountPercent, discountedPrice } from '@/services/discounts';
import { useActiveDiscounts } from '@/hooks/useActiveDiscounts';
import FloatingDonationDog from '@/components/FloatingDonationDog';
import FloatingWhatsapp from '@/components/FloatingWhatsapp';
import CartDrawer from '@/components/feature/CartDrawer';
import SectionLink from '@/components/feature/SectionLink';
import FaqSection from '@/pages/store/landing/components/FaqSection';
import TestimonialsCarousel from '@/pages/store/landing/components/TestimonialsCarousel';

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [contactWhatsapp, setContactWhatsapp] = useState(PCP_OFFICIAL_WHATSAPP);
  const [content, setContent] = useState<LandingContent>(DEFAULT_LANDING_CONTENT);
  const [searchTerm, setSearchTerm] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Avisos configurables de la tienda (banner del catálogo / checkout).
  const [notices, setNotices] = useState<StoreNotices>(DEFAULT_STORE_NOTICES);
  const [catalogInView, setCatalogInView] = useState(false);
  const [catalogNoticeDismissed, setCatalogNoticeDismissed] = useState(false);
  const catalogSectionRef = useRef<HTMLElement | null>(null);

  const { count: cartCount } = useCart();
  const { data: products } = useProducts();
  const { track } = useMetaPixel();
  const { data: variants } = useVariants();
  const { data: categories } = useCategories();
  const discounts = useActiveDiscounts();

  useEffect(() => {
    let cancelled = false;
    getCompanySettings().then((settings) => {
      if (!cancelled && settings.contactWhatsapp) {
        setContactWhatsapp(settings.contactWhatsapp);
      }
    });
    getLandingContent().then((c) => {
      if (!cancelled) setContent(c);
    });
    getStoreNotices().then((n) => {
      if (!cancelled) setNotices(n);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Limpia el temporizador del buscador al desmontar.
  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  // El aviso flotante del catálogo solo se muestra mientras la sección del
  // catálogo está a la vista (al hacer scroll hasta ella).
  useEffect(() => {
    const el = catalogSectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setCatalogInView(entry.isIntersecting),
      { threshold: 0.12 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Filtrado del catálogo por búsqueda (nombre, referencia o descripción).
  const activeProducts = products.filter((p) => p.active);
  const normalizedQuery = searchTerm.trim().toLowerCase();
  const filteredProducts = normalizedQuery
    ? activeProducts.filter(
        (p) =>
          p.name.toLowerCase().includes(normalizedQuery) ||
          (p.description ?? '').toLowerCase().includes(normalizedQuery) ||
          (p.sku ?? '').toLowerCase().includes(normalizedQuery),
      )
    : activeProducts;

  // Al escribir en el buscador se dispara el evento Search (con rebote para no
  // saturar: solo cuando la consulta tiene 2+ caracteres y el cliente pausa).
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const query = value.trim();
    if (query.length < 2) return;
    searchTimerRef.current = setTimeout(() => {
      track('Search', { search_string: query });
    }, 700);
  };

  // El salto a las secciones con ancla (#catalogo, #testimonios, ...) lo maneja
  // el ScrollManager global, así funciona igual desde cualquier página.

  return (
    <div id="top" className="relative z-0 min-h-screen bg-background-50">
      <div className="paw-watermark absolute inset-0 -z-10 pointer-events-none opacity-[0.04]" aria-hidden="true" />
      {/* NAVBAR */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="#top" className="flex items-center gap-2.5 flex-shrink-0">
              <div className="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center">
                <i className="ri-heart-pulse-line text-white text-base"></i>
              </div>
              <span className="text-lg font-bold text-foreground-950 tracking-tight">Paticas con Propósito</span>
            </a>

            <div className="hidden md:flex items-center gap-8">
              <a href="#top" className="text-sm font-medium text-accent-600">Inicio</a>
              <a href="#catalogo" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors">Catálogo</a>
              <a href="#testimonios" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors">Testimonios</a>
              <a href="#preguntas" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors">Preguntas</a>
              <a href="#proposito" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors">Propósito</a>
              <a href="#contacto" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors">Contacto</a>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={() => setCartOpen(!cartOpen)} className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-shopping-bag-3-line text-foreground-700 text-lg"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-accent-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{cartCount}</span>
                )}
              </button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors">
                <i className={`text-foreground-700 text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200 bg-white">
            <div className="px-4 py-3 space-y-1">
              <a href="#top" className="block px-3 py-2.5 rounded-lg text-sm font-medium text-accent-600 bg-accent-50">Inicio</a>
              <a href="#catalogo" className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-50">Catálogo</a>
              <a href="#testimonios" className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-50">Testimonios</a>
              <a href="#preguntas" className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-50">Preguntas</a>
              <a href="#proposito" className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-50">Propósito</a>
              <a href="#contacto" className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-50">Contacto</a>
            </div>
          </div>
        )}
      </nav>

      {/* Mini-carrito compartido (con eliminar productos, cantidades y vaciar) */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

      {/* Aviso flotante del catálogo — aplica solo a la línea Premium */}
      {notices.catalogEnabled && catalogInView && !catalogNoticeDismissed && (
        <div className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-xl animate-toast-pop">
          <div className="relative flex items-start gap-3 bg-primary-500 text-foreground-950 rounded-2xl border border-primary-600 px-4 py-3.5 pr-10">
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 flex-shrink-0">
              <i className="ri-t-shirt-2-line text-primary-700 text-lg"></i>
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm leading-relaxed font-medium whitespace-pre-line">{notices.catalogText}</p>
              <p className="text-xs mt-1 flex items-center gap-1.5 text-foreground-800">
                <i className="ri-information-line"></i>
                Aplica exclusivamente para las babuchas de la línea Premium.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCatalogNoticeDismissed(true)}
              className="absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center rounded-full text-foreground-800 hover:bg-background-50/70 transition-colors cursor-pointer"
              aria-label="Cerrar aviso del catálogo"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative pt-16">
        <div className="relative h-[500px] md:h-[620px] w-full overflow-hidden bg-foreground-900">
          {content.heroMediaType === 'image' && content.heroMediaUrl && (
            <img
              src={content.heroMediaUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          )}
          {content.heroMediaType === 'video' && content.heroMediaUrl && (
            <video
              src={content.heroMediaUrl}
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            />
          )}
          {(content.heroMediaType === 'image' || content.heroMediaType === 'video') && content.heroMediaUrl && (
            <div className={`absolute inset-0 ${getHeroOverlayClass(content.heroOverlayIntensity)}`}></div>
          )}
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl mx-auto text-center">
                <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full mb-4">Hecho con amor en Colombia</span>
                <h1
                  className={`${getHeroTitleSizeClass(content.heroTitleSize)} ${getTextWeightClass(content.heroTitleWeight)} leading-tight mb-4`}
                  style={{
                    color: content.heroTextColor,
                    textShadow: getHeroTextShadow(content.heroTextShadow, content.heroTextShadowColor),
                    fontFamily: getHeroFontFamily(content.heroTextFont),
                  }}
                >
                  {content.heroTitle}
                </h1>
                <p
                  className="text-base sm:text-lg max-w-xl mx-auto mb-8"
                  style={{
                    color: content.heroTextColor,
                    opacity: 0.88,
                    textShadow: getHeroTextShadow(content.heroTextShadow, content.heroTextShadowColor),
                    fontFamily: getHeroFontFamily(content.heroTextFont),
                  }}
                >
                  {content.heroSubtitle}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href="#catalogo" className="px-8 py-3.5 bg-accent-500 text-white rounded-full font-semibold hover:bg-primary-500 hover:text-foreground-950 transition-all text-sm whitespace-nowrap shadow-lg shadow-accent-500/25">
                    Ver Catálogo
                  </a>
                  <a href="#proposito" className="px-8 py-3.5 bg-white/15 backdrop-blur-sm text-white border border-white/30 rounded-full font-semibold hover:bg-white/25 transition-all text-sm whitespace-nowrap">
                    Nuestro Propósito
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PREGUNTAS FRECUENTES */}
      <FaqSection />

      {/* CATÁLOGO */}
      <section id="catalogo" ref={catalogSectionRef} className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto scroll-mt-16">
        <div className="text-center mb-12">
          <h2
            className={`${getSectionTitleSizeClass(content.sectionTitleSize)} ${getTextWeightClass(content.sectionTitleWeight)} mb-3`}
            style={{
              color: content.sectionTextColor,
              textShadow: getHeroTextShadow(content.sectionTextShadow, content.sectionTextShadowColor),
              fontFamily: getHeroFontFamily(content.sectionTextFont),
            }}
          >
            {content.referenciasTitle}
          </h2>
          <p className="text-foreground-600 max-w-lg mx-auto text-sm md:text-base">{content.referenciasSubtitle}</p>
          <div className="flex items-center justify-center gap-3 mt-5">
            <span className="h-px w-16 bg-background-200"></span>
            <span className="w-8 h-8 flex items-center justify-center text-primary-500"><i className="ri-footprint-line text-2xl"></i></span>
            <span className="h-px w-16 bg-background-200"></span>
          </div>
        </div>

        {(content.referenciasMediaType === 'image' || content.referenciasMediaType === 'video') && content.referenciasMediaUrl && (
          <div className="mb-8 rounded-2xl overflow-hidden">
            {content.referenciasMediaType === 'image' ? (
              <img src={content.referenciasMediaUrl} alt="" className="w-full h-64 md:h-80 object-cover object-top" />
            ) : (
              <video src={content.referenciasMediaUrl} className="w-full h-64 md:h-80 object-cover" autoPlay muted loop playsInline controls />
            )}
          </div>
        )}

        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400 pointer-events-none">
              <i className="ri-search-line text-lg"></i>
            </span>
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
              placeholder="Busca por nombre, referencia o descripción..."
              aria-label="Buscar productos"
              className="w-full text-sm border border-background-300 rounded-full pl-11 pr-10 py-3 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                aria-label="Limpiar búsqueda"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            )}
          </div>
          {normalizedQuery && (
            <p className="text-xs text-foreground-500 mt-2.5 text-center">
              {filteredProducts.length}{' '}
              {filteredProducts.length === 1 ? 'resultado' : 'resultados'} para{' '}
              <strong className="text-foreground-800">{searchTerm.trim()}</strong>
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredProducts.map(product => {
            const cat = categories.find(c => c.id === product.categoryId);
            const productSizes = variants.filter(v => v.productId === product.id);
            const pct = getProductDiscountPercent(product.id, discounts, product.lineType);
            const finalPrice = discountedPrice(product.commercialPrice, pct);
            return (
              <Link key={product.id} to={`/producto/${product.slug}`} className="bg-white rounded-2xl border border-background-200 overflow-hidden group hover:border-accent-300 transition-all" data-product-shop>
                <div className="aspect-square bg-background-100 relative overflow-hidden">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      title={`${product.name} babuchas`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-footprint-line text-foreground-300 text-5xl group-hover:scale-110 transition-transform duration-500"></i>
                    </div>
                  )}
                  <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-foreground-800 text-[10px] font-medium px-2.5 py-1 rounded-full">{cat?.name}</span>
                  <span className="absolute top-3 right-3 bg-primary-500 text-foreground-950 text-[10px] font-bold px-2.5 py-1 rounded-full">{product.chargeShipping ? 'Envío calculado' : 'Envío gratis'}</span>
                  {pct > 0 && (
                    <span className="absolute bottom-3 left-3 bg-red-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">-{pct}%</span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-foreground-900 text-sm group-hover:text-accent-600 transition-colors">{product.name}</h3>
                  <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{product.description}</p>
                  {productSizes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {productSizes.map((s) => (
                        <span
                          key={s.id}
                          className="inline-flex flex-col items-center px-2 py-1 rounded-lg bg-background-100 text-foreground-600 font-medium whitespace-nowrap"
                        >
                          <span className="text-[11px] leading-tight">{s.sizeRange ? `Talla ${s.sizeRange}` : s.sizeName}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-100">
                    <span className="text-lg font-bold text-foreground-950">${finalPrice.toLocaleString()}</span>
                    {pct > 0 && (
                      <span className="text-xs text-foreground-400 line-through">${product.commercialPrice.toLocaleString()}</span>
                    )}
                    <span className="text-xs font-medium text-accent-600">Ver detalle</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {normalizedQuery && filteredProducts.length === 0 && (
          <div className="text-center py-14">
            <span className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="text-foreground-700 text-sm font-medium mb-1">
              No encontramos productos para “{searchTerm.trim()}”
            </p>
            <p className="text-foreground-500 text-xs">Prueba con otra referencia o palabra clave.</p>
          </div>
        )}
      </section>

      {/* TESTIMONIOS DE LA MANADA */}
      <TestimonialsCarousel />

      {/* PROPÓSITO */}
      <section id="proposito" className="py-16 md:py-20 bg-background-100 scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-xs font-semibold text-accent-600 uppercase tracking-wider">Nuestro Propósito</span>
              <h2
                className={`${getSectionTitleSizeClass(content.sectionTitleSize)} ${getTextWeightClass(content.sectionTitleWeight)} mt-3 mb-4`}
                style={{
                  color: content.sectionTextColor,
                  textShadow: getHeroTextShadow(content.sectionTextShadow, content.sectionTextShadowColor),
                  fontFamily: getHeroFontFamily(content.sectionTextFont),
                }}
              >
                {content.propositoTitle}
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-6">
                {content.propositoText}
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-heart-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground-900 text-sm">Rescate y rehabilitación</p>
                    <p className="text-sm text-foreground-500">Apoyamos organizaciones que rescatan animales abandonados y los preparan para adopción.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-hospital-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground-900 text-sm">Atención veterinaria</p>
                    <p className="text-sm text-foreground-500">Contribuimos a que animales en fundaciones reciban la atención médica que necesitan.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-home-smile-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground-900 text-sm">Hogares responsables</p>
                    <p className="text-sm text-foreground-500">Promovemos la adopción responsable y la tenencia consciente de animales.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              {content.propositoMediaType === 'image' && content.propositoMediaUrl ? (
                <img src={content.propositoMediaUrl} alt="" className="aspect-[4/3] w-full rounded-2xl object-cover object-top" />
              ) : content.propositoMediaType === 'video' && content.propositoMediaUrl ? (
                <video src={content.propositoMediaUrl} className="aspect-[4/3] w-full rounded-2xl object-cover" autoPlay muted loop playsInline controls />
              ) : (
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-background-200 flex items-center justify-center">
                  <i className="ri-heart-pulse-line text-accent-300 text-8xl"></i>
                </div>
              )}
              <div className="absolute -top-4 right-2 md:right-4 bg-accent-500 text-white rounded-2xl px-5 py-3.5 md:px-6 md:py-4 shadow-lg">
                <p className="text-2xl font-bold">2%</p>
                <p className="text-xs text-white/80">de cada venta</p>
                <p className="text-xs text-white/80">para fundaciones</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">{content.ctaTitle}</h2>
          <p className="text-foreground-600 mb-8">{content.ctaSubtitle}</p>
          <a href="#catalogo" className="inline-block px-10 py-4 bg-accent-500 text-white rounded-full font-semibold hover:bg-primary-500 hover:text-foreground-950 transition-all text-sm whitespace-nowrap shadow-lg shadow-accent-500/25">
            Comprar Ahora
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contacto" className="bg-foreground-950 text-white py-12 px-4 sm:px-6 lg:px-8 scroll-mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
                  <i className="ri-heart-pulse-line text-white text-sm"></i>
                </div>
                <span className="font-bold text-white">PCP</span>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">{content.footerText}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Tienda</h4>
              <div className="space-y-2.5">
                {categories.map((c) => (
                  <SectionLink key={c.id} section="catalogo" className="block text-sm text-white/60 hover:text-white transition-colors">{c.name}</SectionLink>
                ))}
                {categories.length === 0 && (
                  <SectionLink section="catalogo" className="block text-sm text-white/60 hover:text-white transition-colors">Ver catálogo</SectionLink>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Enlaces</h4>
              <div className="space-y-2.5">
                <a href="#catalogo" className="block text-sm text-white/60 hover:text-white transition-colors">Catálogo</a>
                <a href="#testimonios" className="block text-sm text-white/60 hover:text-white transition-colors">Testimonios</a>
                <a href="#proposito" className="block text-sm text-white/60 hover:text-white transition-colors">Propósito</a>
                <a href="#contacto" className="block text-sm text-white/60 hover:text-white transition-colors">Contacto</a>
                <Link to="/pedidos" className="block text-sm text-white/60 hover:text-white transition-colors">Mis pedidos</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Contacto</h4>
              <div className="space-y-2.5 text-sm text-white/60">
                <a href={`https://wa.me/${contactWhatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer noopener" onClick={() => track('Lead', { content_name: 'WhatsApp', currency: 'COP', value: 0 })} className="text-white/60 hover:text-white transition-colors"><i className="ri-whatsapp-line mr-1.5"></i>{contactWhatsapp}</a>
                {content.contactEmail && (
                  <p><i className="ri-mail-line mr-1.5"></i>{content.contactEmail}</p>
                )}
                {content.contactCity && (
                  <p><i className="ri-map-pin-line mr-1.5"></i>{content.contactCity}</p>
                )}
                {content.contactAddress && (
                  <p><i className="ri-map-pin-2-line mr-1.5"></i>{content.contactAddress}</p>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-white/40">© 2026 PCP — Paticas con Propósito. Todos los derechos reservados.</p>
            <div className="flex items-center gap-4">
              {content.facebookUrl && (
                <a href={content.facebookUrl} target="_blank" rel="noreferrer noopener" className="text-white/40 hover:text-white transition-colors" aria-label="Facebook"><i className="ri-facebook-line text-lg"></i></a>
              )}
              {content.instagramUrl && (
                <a href={content.instagramUrl} target="_blank" rel="noreferrer noopener" className="text-white/40 hover:text-white transition-colors" aria-label="Instagram"><i className="ri-instagram-line text-lg"></i></a>
              )}
              {content.tiktokUrl && (
                <a href={content.tiktokUrl} target="_blank" rel="noreferrer noopener" className="text-white/40 hover:text-white transition-colors" aria-label="TikTok"><i className="ri-tiktok-line text-lg"></i></a>
              )}
              <a href={`https://wa.me/${contactWhatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer noopener" onClick={() => track('Lead', { content_name: 'WhatsApp', currency: 'COP', value: 0 })} className="text-white/40 hover:text-white transition-colors" aria-label="WhatsApp"><i className="ri-whatsapp-line text-lg"></i></a>
            </div>
          </div>
        </div>
      </footer>

      <FloatingDonationDog />
      <FloatingWhatsapp />
    </div>
  );
}